'use client'

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { Socket } from 'socket.io-client'
import { usePathname } from 'next/navigation'
import { getSocket, disconnectSocket } from '@/lib/socket'
import { useUser } from '@/contexts/UserContext'
import { useToast } from '@/components/ui/ToastContainer'

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://app.canerozen.com/api'
const PINNED_STORAGE_KEY = 'chat_drawer_pinned'

function isChatPinnedStored() {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem(PINNED_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export type OnlineUser = {
  id: number
  name: string
  email: string
  firm_id: number | null
  last_seen: number
}

export type ChatMessage = {
  id: number
  created_at: number
  from_user_id: number
  to_user_id: number
  message: string
  attachments?: ChatAttachment[]
  read_at: number
}

export type ChatAttachment = {
  id?: number
  name: string
  mime: string
  path: string
  size?: number
  type?: string
}

export type ChatNotifySettings = {
  soundEnabled: boolean
  toastEnabled: boolean
  toastPreviewEnabled: boolean
  toastAutoCloseEnabled: boolean
}

type ChatRealtimeContextValue = {
  socket: Socket | null
  socketConnected: boolean
  onlineUsers: OnlineUser[]
  onlineUserIdSet: Set<number>
  onlineVersion: number

  unreadTotal: number
  syncUnreadTotal: () => Promise<void>
  clearUnreadOptimistic: () => void

  drawerOpen: boolean
  chatPinned: boolean
  openDrawer: () => void
  closeDrawer: () => void
  setChatPinned: (next: boolean) => void
  openDrawerWithUser: (userId: number) => void
  requestedOpenUserId: number | null
  requestedOpenUserSeq: number

  activeChatUserId: number | null
  setActiveChatUserId: (id: number | null) => void

  notifySettings: ChatNotifySettings
  setNotifySettings: (next: Partial<ChatNotifySettings>) => void

  lastMessage: ChatMessage | null
  lastMessageSeq: number

  sendMessage: (toUserId: number, message: string, attachments?: ChatAttachment[]) => Promise<ChatMessage | null>
  markRead: (withUserId: number) => Promise<void>
}

const ChatRealtimeContext = createContext<ChatRealtimeContextValue | undefined>(undefined)

function safeInt(v: any) {
  const n = typeof v === 'number' ? v : parseInt(v, 10)
  return Number.isFinite(n) ? n : 0
}

function initials(nameOrEmail: string) {
  const n = String(nameOrEmail || '').trim()
  if (!n) return '?'
  const parts = n.split(/\s+/).filter(Boolean)
  const a = (parts[0] || '').slice(0, 1)
  const b = (parts[1] || '').slice(0, 1)
  return (a + b).toUpperCase() || a.toUpperCase() || '?'
}

function avatarColor(seed: string) {
  const s = String(seed || '').trim().toLowerCase()
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0
  }
  const hue = h % 360
  return `hsl(${hue} 62% 32%)`
}

function formatTimeShort(ts: number) {
  const n = typeof ts === 'number' ? ts : safeInt(ts)
  if (!n) return ''
  try {
    const d = new Date(n)
    if (!Number.isFinite(d.getTime())) return ''
    return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

export function ChatRealtimeProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const isSupportDomain =
    typeof window !== 'undefined' &&
    ['support.canerozen.com', 'www.support.canerozen.com'].includes(window.location.hostname)
  if (isSupportDomain || pathname === '/support' || pathname?.startsWith('/support/')) {
    return <ChatRealtimeDisabled>{children}</ChatRealtimeDisabled>
  }
  return <ChatRealtimeProviderInner>{children}</ChatRealtimeProviderInner>
}

function ChatRealtimeDisabled({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    disconnectSocket()
  }, [])
  return <>{children}</>
}

function ChatRealtimeProviderInner({ children }: { children: React.ReactNode }) {
  const { user, refreshUser } = useUser()
  const { showToast, dismissToast } = useToast()

  const socketRef = useRef<Socket | null>(null)
  const [socketConnected, setSocketConnected] = useState(false)

  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([])
  const [onlineVersion, setOnlineVersion] = useState(0)

  const [unreadTotal, setUnreadTotal] = useState(0)

  const [drawerOpen, setDrawerOpen] = useState(() => {
    return isChatPinnedStored()
  })
  const [chatPinned, setChatPinnedState] = useState(() => {
    return isChatPinnedStored()
  })
  const drawerOpenRef = useRef(false)
  useEffect(() => {
    drawerOpenRef.current = drawerOpen
  }, [drawerOpen])

  const setChatPinned = useCallback((next: boolean) => {
    setChatPinnedState(next)
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(PINNED_STORAGE_KEY, next ? '1' : '0')
      } catch {
        // ignore
      }
    }
    if (next) {
      setDrawerOpen(true)
    }
  }, [])

  useEffect(() => {
    if (isChatPinnedStored()) {
      setChatPinnedState(true)
      setDrawerOpen(true)
    }
  }, [])

  useEffect(() => {
    if (user && isChatPinnedStored()) {
      setChatPinnedState(true)
      setDrawerOpen(true)
    }
  }, [user])

  const [requestedOpenUserId, setRequestedOpenUserId] = useState<number | null>(null)
  const [requestedOpenUserSeq, setRequestedOpenUserSeq] = useState(0)

  const [activeChatUserId, setActiveChatUserId] = useState<number | null>(null)
  const activeChatUserIdRef = useRef<number | null>(null)
  useEffect(() => {
    activeChatUserIdRef.current = activeChatUserId
  }, [activeChatUserId])

  const [notifySettings, setNotifySettingsState] = useState<ChatNotifySettings>({
    soundEnabled: true,
    toastEnabled: true,
    toastPreviewEnabled: true,
    toastAutoCloseEnabled: false,
  })

  // Keep a single sticky toast per sender.
  const chatToastByUserIdRef = useRef<Map<number, string>>(new Map())

  // Cache user meta from presence snapshots so notifications can show name/avatar.
  const userCacheRef = useRef<Map<number, { name: string; email?: string }>>(new Map())

  // Audio unlock: many browsers block WebAudio until a user gesture.
  const audioCtxRef = useRef<AudioContext | null>(null)
  const audioUnlockedRef = useRef(false)

  const unlockAudio = useCallback(async () => {
    if (audioUnlockedRef.current) return
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext
      if (!AudioCtx) return
      const ctx: AudioContext = audioCtxRef.current || new AudioCtx()
      audioCtxRef.current = ctx
      if (ctx.state === 'suspended') {
        await ctx.resume().catch(() => {})
      }
      // Play a tiny silent tick to unlock on iOS-like environments.
      const o = ctx.createOscillator()
      const g = ctx.createGain()
      g.gain.value = 0.0001
      o.connect(g)
      g.connect(ctx.destination)
      o.start()
      o.stop(ctx.currentTime + 0.01)
      audioUnlockedRef.current = true
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    const onAnyGesture = () => {
      unlockAudio().catch(() => {})
    }
    document.addEventListener('pointerdown', onAnyGesture, { passive: true })
    document.addEventListener('keydown', onAnyGesture, { passive: true })
    return () => {
      document.removeEventListener('pointerdown', onAnyGesture)
      document.removeEventListener('keydown', onAnyGesture)
    }
  }, [unlockAudio])

  const playTin = useCallback(() => {
    try {
      const ctx = audioCtxRef.current
      if (!ctx) return
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {})
      }
      const o = ctx.createOscillator()
      const g = ctx.createGain()
      o.type = 'sine'
      o.frequency.value = 880
      g.gain.value = 0.035
      o.connect(g)
      g.connect(ctx.destination)
      o.start()
      window.setTimeout(() => {
        try { o.stop() } catch {}
      }, 120)
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    try {
      const sound = localStorage.getItem('chat_notify_sound')
      const toast = localStorage.getItem('chat_notify_toast')
      const preview = localStorage.getItem('chat_notify_toast_preview')
      const autoClose = localStorage.getItem('chat_notify_toast_auto_close')
      setNotifySettingsState({
        soundEnabled: sound == null ? true : sound === '1',
        // Default ON unless user explicitly disables.
        toastEnabled: toast == null ? true : toast === '1',
        toastPreviewEnabled: preview == null ? true : preview === '1',
        toastAutoCloseEnabled: autoClose === '1',
      })
    } catch {
      // ignore
    }
  }, [])

  const setNotifySettings = useCallback((next: Partial<ChatNotifySettings>) => {
    setNotifySettingsState((prev) => {
      const merged = { ...prev, ...next }
      try {
        localStorage.setItem('chat_notify_sound', merged.soundEnabled ? '1' : '0')
        localStorage.setItem('chat_notify_toast', merged.toastEnabled ? '1' : '0')
        localStorage.setItem('chat_notify_toast_preview', merged.toastPreviewEnabled ? '1' : '0')
        localStorage.setItem('chat_notify_toast_auto_close', merged.toastAutoCloseEnabled ? '1' : '0')
      } catch {
        // ignore
      }
      return merged
    })
  }, [])

  const clearChatToasts = useCallback(() => {
    const map = chatToastByUserIdRef.current
    if (!map.size) return
    chatToastByUserIdRef.current = new Map()
    for (const id of map.values()) {
      try { dismissToast(id) } catch {}
    }
  }, [dismissToast])

  const openDrawer = useCallback(() => {
    clearChatToasts()
    setDrawerOpen(true)
  }, [clearChatToasts])

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false)
    setActiveChatUserId(null)
  }, [])

  const openDrawerWithUser = useCallback(
    (userId: number) => {
      const id = safeInt(userId)
      if (!id) return
      clearChatToasts()
      setRequestedOpenUserId(id)
      setRequestedOpenUserSeq((n) => n + 1)
      setDrawerOpen(true)
    },
    [clearChatToasts]
  )

  const [lastMessage, setLastMessage] = useState<ChatMessage | null>(null)
  const [lastMessageSeq, setLastMessageSeq] = useState(0)

  const consecutiveSocketErrorsRef = useRef(0)

  const onlineUserIdSet = useMemo(() => new Set(onlineUsers.map((u) => u.id)), [onlineUsers])

  const getToken = useCallback(() => {
    if (typeof window === 'undefined') return null
    return localStorage.getItem('auth_token')
  }, [])

  const syncUnreadTotal = useCallback(async () => {
    const token = getToken()
    if (!token) return
    const res = await fetch(`${API_BASE}/chat/unread-count`, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => null)
    if (!res) return
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data?.success) return
    const v = safeInt(data?.data?.unread)
    setUnreadTotal(Number.isFinite(v) ? v : 0)
  }, [getToken])

  const clearUnreadOptimistic = useCallback(() => {
    setUnreadTotal(0)
  }, [])

  const markRead = useCallback(
    async (withUserId: number) => {
      const token = getToken()
      if (!token || !withUserId) return

      const s = socketRef.current
      if (s && s.connected) {
        await new Promise<void>((resolve) => {
          try {
            s.emit('chat:read', { with_user_id: withUserId }, () => resolve())
          } catch {
            resolve()
          }
        })
        return
      }

      await fetch(`${API_BASE}/chat/messages/${withUserId}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {})
    },
    [getToken]
  )

  const sendMessage = useCallback(
    async (toUserId: number, message: string, attachments: ChatAttachment[] = []) => {
      const token = getToken()
      if (!token || !toUserId) return null
      const text = String(message || '').trim()
      const files = Array.isArray(attachments) ? attachments : []
      if (!text && files.length === 0) return null

      const s = socketRef.current
      if (s && s.connected) {
        const ack = await new Promise<any>((resolve) => {
          try {
            s.emit('chat:send', { to_user_id: toUserId, message: text, attachments: files }, (a: any) => resolve(a))
          } catch {
            resolve(null)
          }
        })
        if (ack?.ok && ack?.data) return ack.data as ChatMessage
        return null
      }

      // REST fallback (single call, no polling)
      const res = await fetch(`${API_BASE}/chat/messages/${toUserId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: text, attachments: files }),
      }).catch(() => null)
      if (!res) return null
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data?.success) return null
      return data.data as ChatMessage
    },
    [getToken]
  )

  useEffect(() => {
    if (!user) {
      setSocketConnected(false)
      setOnlineUsers([])
      setOnlineVersion((v) => v + 1)
      setUnreadTotal(0)
      if (!isChatPinnedStored()) {
        setDrawerOpen(false)
      }
      setRequestedOpenUserId(null)
      setRequestedOpenUserSeq(0)
      setLastMessage(null)
      setLastMessageSeq(0)
      consecutiveSocketErrorsRef.current = 0
      socketRef.current = null
      userCacheRef.current = new Map()
      chatToastByUserIdRef.current = new Map()
      audioUnlockedRef.current = false
      audioCtxRef.current = null
      disconnectSocket()
      return
    }

    const token = getToken()
    if (!token) return

    const s = getSocket(token)
    socketRef.current = s

    const setSortedOnline = (list: any[]) => {
      const normalized: OnlineUser[] = (Array.isArray(list) ? list : [])
        .filter(Boolean)
        .map((u: any) => ({
          id: safeInt(u.id),
          name: String(u.name || ''),
          email: String(u.email || ''),
          firm_id: u.firm_id == null ? null : safeInt(u.firm_id),
          last_seen: safeInt(u.last_seen) || Date.now(),
        }))
        .filter((u) => !!u.id)
        .sort((a, b) => (b.last_seen || 0) - (a.last_seen || 0))
        .slice(0, 200)

      setOnlineUsers(normalized)
      setOnlineVersion((v) => v + 1)

      // warm cache for notifications
      const cache = userCacheRef.current
      for (const u of normalized) {
        if (u.id) cache.set(u.id, { name: u.name, email: u.email })
      }
    }

    const onConnect = () => {
      consecutiveSocketErrorsRef.current = 0
      setSocketConnected(true)
      try {
        s.emit('online:refresh')
      } catch {
        // ignore
      }
    }

    const onDisconnect = () => {
      setSocketConnected(false)
    }

    const onConnectError = async (err: any) => {
      consecutiveSocketErrorsRef.current += 1
      setSocketConnected(false)

      // If auth breaks, stop the reconnect loop by clearing tokens.
      const msg = String(err?.message || '')
      if (msg.toLowerCase().includes('unauthorized')) {
        try {
          localStorage.removeItem('auth_token')
          localStorage.removeItem('refresh_token')
        } catch {}
        await refreshUser().catch(() => {})
      }
    }

    const onInit = (list: any) => {
      if (!Array.isArray(list)) return
      setSortedOnline(list)
    }

    const onPresenceUpdate = (u: any) => {
      if (!u?.id) return
      try {
        const id = safeInt(u.id)
        if (id) {
          userCacheRef.current.set(id, { name: String(u.name || ''), email: String(u.email || '') })
        }
      } catch {
        // ignore
      }
      setOnlineUsers((prev) => {
        const map = new Map<number, OnlineUser>()
        for (const p of prev) map.set(p.id, p)
        const id = safeInt(u.id)
        if (!id) return prev
        map.set(id, {
          id,
          name: String(u.name || ''),
          email: String(u.email || ''),
          firm_id: u.firm_id == null ? null : safeInt(u.firm_id),
          last_seen: safeInt(u.last_seen) || Date.now(),
        })
        const next = Array.from(map.values()).sort((a, b) => (b.last_seen || 0) - (a.last_seen || 0)).slice(0, 200)
        return next
      })
      setOnlineVersion((v) => v + 1)
    }

    const onPresenceOffline = (p: any) => {
      const id = safeInt(p?.user_id)
      if (!id) return
      setOnlineUsers((prev) => prev.filter((x) => x.id !== id))
      setOnlineVersion((v) => v + 1)
    }

    const onChatMessage = (m: any) => {
      const msg: ChatMessage = {
        id: safeInt(m?.id),
        created_at: safeInt(m?.created_at) || Date.now(),
        from_user_id: safeInt(m?.from_user_id),
        to_user_id: safeInt(m?.to_user_id),
        message: String(m?.message || ''),
        attachments: Array.isArray(m?.attachments) ? m.attachments : [],
        read_at: safeInt(m?.read_at),
      }
      if (!msg.id || !msg.to_user_id || !msg.from_user_id) return

      setLastMessage(msg)
      setLastMessageSeq((n) => n + 1)

      // Unread badge + sound: only for incoming messages.
      if (msg.to_user_id === user.id) {
        const open = !!drawerOpenRef.current
        const activeId = activeChatUserIdRef.current
        const messageFromActive = open && !!activeId && msg.from_user_id === activeId
        if (!messageFromActive) {
          setUnreadTotal((prev) => prev + 1)
        }

        if (!open && notifySettings.soundEnabled) playTin()

        if (!open && notifySettings.toastEnabled) {
          const fromId = msg.from_user_id
          const prevId = chatToastByUserIdRef.current.get(fromId)
          if (prevId) {
            try { dismissToast(prevId) } catch {}
          }

          const cached = userCacheRef.current.get(fromId)
          const title = cached?.name ? cached.name : `#${fromId}`
          const time = formatTimeShort(msg.created_at)
          const preview = notifySettings.toastPreviewEnabled
            ? (String(msg.message || '').trim() || (msg.attachments?.length ? 'Görsel' : ''))
            : ''

          const toastId = showToast(
            <div className="toast-chat">
              <div
                className="toast-chat__avatar"
                style={{ background: avatarColor(cached?.name || String(fromId)) }}
                aria-hidden
              >
                {initials(cached?.name || String(fromId))}
              </div>
              <div className="toast-chat__body">
                <div className="toast-chat__top">
                  <div className="toast-chat__title">{title}</div>
                  <div className="toast-chat__time">{time}</div>
                </div>
                <div className="toast-chat__preview">{preview}</div>
              </div>
            </div>,
            'info',
            notifySettings.toastAutoCloseEnabled ? 5000 : 0,
            () => openDrawerWithUser(fromId)
          )

          chatToastByUserIdRef.current.set(fromId, toastId)
        }
      }
    }

    s.on('connect', onConnect)
    s.on('disconnect', onDisconnect)
    s.on('connect_error', onConnectError)
    s.on('online:init', onInit)
    s.on('presence:update', onPresenceUpdate)
    s.on('presence:offline', onPresenceOffline)
    s.on('chat:message', onChatMessage)

    // Initial sync (REST once) + socket snapshot
    syncUnreadTotal().catch(() => {})
    try {
      s.emit('online:refresh')
    } catch {}

    // Periodic snapshot refresh over socket (no REST polling)
    const refreshTimer = window.setInterval(() => {
      if (!socketRef.current?.connected) return
      try {
        socketRef.current.emit('online:refresh')
      } catch {
        // ignore
      }
    }, 60000)

    return () => {
      try {
        window.clearInterval(refreshTimer)
      } catch {}

      s.off('connect', onConnect)
      s.off('disconnect', onDisconnect)
      s.off('connect_error', onConnectError)
      s.off('online:init', onInit)
      s.off('presence:update', onPresenceUpdate)
      s.off('presence:offline', onPresenceOffline)
      s.off('chat:message', onChatMessage)
    }
  }, [getToken, notifySettings.soundEnabled, notifySettings.toastAutoCloseEnabled, notifySettings.toastEnabled, notifySettings.toastPreviewEnabled, openDrawerWithUser, refreshUser, showToast, syncUnreadTotal, user])

  const value = useMemo<ChatRealtimeContextValue>(
    () => ({
      socket: socketRef.current,
      socketConnected,
      onlineUsers,
      onlineUserIdSet,
      onlineVersion,
      unreadTotal,
      syncUnreadTotal,
      clearUnreadOptimistic,
      drawerOpen,
      chatPinned,
      openDrawer,
      closeDrawer,
      setChatPinned,
      openDrawerWithUser,
      requestedOpenUserId,
      requestedOpenUserSeq,
      activeChatUserId,
      setActiveChatUserId,
      notifySettings,
      setNotifySettings,
      lastMessage,
      lastMessageSeq,
      sendMessage,
      markRead,
    }),
    [
      activeChatUserId,
      chatPinned,
      closeDrawer,
      clearUnreadOptimistic,
      drawerOpen,
      lastMessage,
      lastMessageSeq,
      markRead,
      notifySettings,
      openDrawer,
      openDrawerWithUser,
      onlineUserIdSet,
      onlineUsers,
      onlineVersion,
      requestedOpenUserId,
      requestedOpenUserSeq,
      sendMessage,
      socketConnected,
      setNotifySettings,
      setChatPinned,
      syncUnreadTotal,
      unreadTotal,
    ]
  )

  return <ChatRealtimeContext.Provider value={value}>{children}</ChatRealtimeContext.Provider>
}

export function useChatRealtime() {
  const ctx = useContext(ChatRealtimeContext)
  if (!ctx) throw new Error('useChatRealtime must be used within a ChatRealtimeProvider')
  return ctx
}
